import React, { useEffect, useState } from 'react';
import { seChoicesService } from '../../../_services/seChoices.service';
import { CHOICE_COLUMNS, choiceChips, onlyTheyCanChange } from './choiceChips';
import { DISCOUNTS_VISIBLE } from '../../../config/featureFlags';
import { loadChoices } from '../../discounts/discountStore';
import './TheirChoices.css';

/**
 * Team › Members › Their choices: what each self-employed stylist has chosen
 * (client sharing, diary, marketing, loyalty, booking page, prices,
 * payments), read-only. Only the stylist can change any of it; tapping a
 * chip says so. Shows the CHOICE only, never their clients.
 *
 * For the owner and anyone whose role has `team` (Staff.js decides; the
 * server refuses everyone else).
 */

const initials = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

export default function TheirChoices({ search = '' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState('');

  useEffect(() => {
    let off = false;
    setError('');
    seChoicesService.team()
      // Discount codes: their answers, from the server's row when it sends
      // one, else the device stand-in (staging)
      .then((d) => (DISCOUNTS_VISIBLE ? loadChoices().then((codes) => ({
        ...d,
        stylists: (d.stylists || []).map((st) => ({ ...st, choices: { ...(st.choices || {}), codes: (st.choices && st.choices.codes) || codes[String(st.userId)] || codes[String(st.hairdresserId)] || null } })),
      })).catch(() => d) : d))
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setError((e && e.plain && e.message) || 'I couldn’t load their choices just now.'); });
    return () => { off = true; };
  }, [attempt]);

  if (error && !data) {
    return (
      <div className="tc-empty">
        <p>{error}</p>
        <button type="button" className="staff-retry-btn" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
      </div>
    );
  }
  if (!data) return <div className="tc-empty" aria-busy="true"><p>Loading their choices…</p></div>;

  const q = search.trim().toLowerCase();
  const rows = (data.stylists || []).filter((s) => !q || String(s.name || '').toLowerCase().includes(q));

  return (
    <div className="tc-wrap">
      <p className="tc-lede">Choices each self-employed stylist has made. Only they can change them; you see the choice, never their private clients.</p>
      {rows.length === 0 ? (
        <div className="tc-empty"><p>{q ? 'No self-employed stylists match.' : 'No self-employed stylists here yet.'}</p></div>
      ) : (
        <div className="tc-tablewrap" tabIndex={0} role="region" aria-label="Self-employed choices">
          <table className="tc-table">
            <thead>
              <tr>
                <th scope="col">Stylist</th>
                {CHOICE_COLUMNS.map((c) => <th scope="col" key={c.key}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.hairdresserId}>
                  <td>
                    <div className="tc-who">
                      {s.image ? <img className="tc-av" src={s.image} alt="" /> : <span className="tc-av">{initials(s.name)}</span>}
                      <div>{s.name}<small>Self-employed</small></div>
                    </div>
                  </td>
                  {choiceChips(s.choices).map((c) => (
                    <td key={c.key} data-label={c.column}>
                      <button
                        type="button"
                        className={`tc-chip tc-chip--${c.tone}`}
                        title={c.hint}
                        aria-label={`${c.column}: ${c.label}. ${c.hint} ${onlyTheyCanChange(s.name)}`}
                        onClick={() => setSaid(`${s.name} · ${c.column}: ${c.hint} ${onlyTheyCanChange(s.name)}`)}
                      >
                        {c.label}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="tc-lock" aria-live="polite">{said || 'Tap any choice to see what it means. Only the stylist can change it.'}</p>
    </div>
  );
}
